import { useState, useEffect } from 'react';
import axios from 'axios';
import { Label } from '@/components/ui/label';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronsUpDown, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Multi-select for restaurants using the same /api/restaurants/search
 * endpoint used by RestaurantSelector.
 */
export const MultiRestaurantSelector = ({
  value = [], // array of ids
  onChange,
  label = 'Restaurants',
  // Optional [{id, name}] the parent already has (e.g. the list API's
  // populated `resturants`) — chips show names instantly in edit mode.
  initialSelected = []
}) => {
  const [open, setOpen] = useState(false);
  const [restaurants, setRestaurants] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(() =>
    (initialSelected || [])
      .filter((r) => r && r.id && (value || []).includes(String(r.id)))
      .map((r) => ({ id: String(r.id), name: r.name || '' }))
  ); // [{id,name,isDeleted?}]
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchRestaurants();
    }, 400);
    return () => clearTimeout(delay);
  }, [searchQuery]);

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/restaurants/search`, {
        params: { q: searchQuery }
      });
      setRestaurants(response.data || []);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  // Names for ids given by the parent (edit mode). One batch request for
  // whatever isn't known yet — never shows a raw id as a label.
  useEffect(() => {
    const ids = (value || []).map(String);
    const known = new Set(selected.map((s) => s.id));
    const missing = ids.filter((id) => !known.has(id));

    if (missing.length === 0) {
      const filtered = selected.filter((s) => ids.includes(s.id));
      if (filtered.length !== selected.length) setSelected(filtered);
      return;
    }

    let cancelled = false;
    setResolving(true);
    axios
      .get(`${API}/restaurants/by-ids`, { params: { ids: missing.join(',') } })
      .then((res) => (Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error('Failed to resolve restaurant names:', err);
        return [];
      })
      .then((found) => {
        if (cancelled) return;
        const foundMap = new Map(found.map((f) => [String(f.id), f]));
        setSelected((prev) => {
          const byId = new Map(prev.map((p) => [p.id, p]));
          missing.forEach((id) => {
            const f = foundMap.get(id);
            byId.set(id, f
              ? { id, name: f.name, isDeleted: f.isDeleted }
              : { id, name: 'Restaurant not found', missing: true });
          });
          return ids.filter((id) => byId.has(id)).map((id) => byId.get(id));
        });
      })
      .finally(() => !cancelled && setResolving(false));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const toggle = (rest) => {
    const exists = value.includes(rest.id);
    const next = exists
      ? value.filter((id) => id !== rest.id)
      : [...value, rest.id];
    onChange(next);
    setSelected((prev) => {
      if (exists) return prev.filter((p) => p.id !== rest.id);
      if (prev.find((p) => p.id === rest.id)) return prev;
      return [...prev, { id: rest.id, name: rest.name }];
    });
  };

  const removeOne = (id) => {
    onChange(value.filter((v) => v !== id));
    setSelected((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <div>
      <Label>{label}</Label>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            data-testid="explore-multi-restaurant-trigger"
            variant="outline"
            role="combobox"
            className="w-full justify-between mt-2"
          >
            {value.length > 0
              ? `${value.length} restaurant${value.length > 1 ? 's' : ''} selected`
              : 'Select restaurants...'}
            <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
          </Button>
        </PopoverTrigger>

        <PopoverContent className="w-[360px] p-0">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search restaurant..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandEmpty>
              {loading ? 'Loading...' : 'No restaurant found.'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-auto">
              {restaurants.map((rest) => {
                const checked = value.includes(rest.id);
                return (
                  <CommandItem
                    key={rest.id}
                    value={rest.name?.toLowerCase() || rest.id}
                    onSelect={() => toggle(rest)}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        checked ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                    <div>
                      <div className="font-medium">{rest.name}</div>
                      {rest.address && (
                        <div className="text-xs text-gray-500">
                          {rest.address}
                        </div>
                      )}
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    
      {(selected.length > 0 || resolving) && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {resolving && selected.length < value.length && (
            <Badge variant="secondary" className="text-gray-500 animate-pulse">
              Loading names…
            </Badge>
          )}
          {selected.map((s) => (
            <Badge
              key={s.id}
              data-testid={`explore-rest-chip-${s.id}`}
              variant="secondary"
              title={s.missing ? `This restaurant no longer exists (id ${s.id}) — remove it` : s.name}
              className={`flex items-center gap-1 pr-1 ${s.missing ? 'bg-red-50 text-red-700' : s.isDeleted ? 'bg-amber-50 text-amber-800' : ''}`}
            >
              {s.name}
              {s.isDeleted && <span className="text-[10px] uppercase ml-0.5">(deleted)</span>}
              <button
                type="button"
                className="rounded-full hover:bg-gray-200 p-0.5"
                onClick={() => removeOne(s.id)}
                aria-label="remove"
              >
                <X size={12} />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

export default MultiRestaurantSelector;
